import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AppStateService } from '../app-state/app-state.service';
import { API_BASE, PROJECT } from '../../constants/general.constants';
import { ChunkContext } from '../../interfaces/chunk/chunk-context.interface';

export interface ProjectStateEntry {
  key: string;
  value: any;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectStateMetadata {
  key: string;
  version: number;
  created_at: string;
  updated_at: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProjectStateService {
  readonly #http = inject(HttpClient);
  readonly #appState = inject(AppStateService);

  private buildUrl(key?: string): string {
    const projectId = this.#appState.projectId();
    const base = `${API_BASE}/${PROJECT}/${projectId}/state`;
    return key ? `${base}/${encodeURIComponent(key)}` : base;
  }

  public async getState(args: any[], context?: ChunkContext): Promise<ProjectStateEntry | null> {
    try {
      if (!args.length || typeof args[0] !== 'string' || !args[0].trim()) {
        const msg = 'getState requires a string key argument';
        if (context) { context.addMessage(msg, 'danger'); context.setChunkResultStatus('error'); }
        throw new Error(msg);
      }
      const key = args[0].trim();
      const r = await firstValueFrom(
        this.#http.get<ProjectStateEntry>(this.buildUrl(key))
      );
      return r;
    } catch (error: any) {
      if (error?.status === 404) { return null; }
      const msg = `Error in getState: ${error.message || error}`;
      if (context) { context.addMessage(msg, 'danger'); context.setChunkResultStatus('error'); }
      throw error;
    }
  }

  public async setState(args: any[], context?: ChunkContext): Promise<ProjectStateEntry> {
    let key: string | undefined;
    let version: number | undefined;
    try {
      if (typeof args[0] !== 'string' || !args[0].trim()) {
        const msg = 'setState requires a string key argument';
        if (context) { context.addMessage(msg, 'danger'); context.setChunkResultStatus('error'); }
        throw new Error(msg);
      }
      if (args.length >= 3 && typeof args[2] !== 'number') {
        const msg = 'setState version must be a number when provided';
        if (context) { context.addMessage(msg, 'danger'); context.setChunkResultStatus('error'); }
        throw new Error(msg);
      }
      key = args[0].trim();
      const value = args[1];
      version = typeof args[2] === 'number' ? args[2] : 0;
      const r = await firstValueFrom(
        this.#http.put<ProjectStateEntry>(this.buildUrl(key), { value, version })
      );
      return r;
    } catch (error: any) {
      const msg = formatStateError('setState', error, { key, version });
      if (context) { context.addMessage(msg, 'danger'); context.setChunkResultStatus('error'); }
      throw new Error(msg);
    }
  }

  public async listState(args: any[], context?: ChunkContext): Promise<ProjectStateMetadata[]> {
    try {
      const r = await firstValueFrom(this.#http.get<ProjectStateMetadata[]>(this.buildUrl()));
      return r;
    } catch (error: any) {
      const msg = `Error in listState: ${error.message || error}`;
      if (context) { context.addMessage(msg, 'danger'); context.setChunkResultStatus('error'); }
      throw error;
    }
  }

  public async deleteState(args: any[], context?: ChunkContext): Promise<boolean> {
    let key: string | undefined;
    let version: number | undefined;
    try {
      if (args.length < 2 || typeof args[0] !== 'string' || !args[0].trim() || typeof args[1] !== 'number') {
        const msg = 'deleteState requires (key: string, version: number)';
        if (context) { context.addMessage(msg, 'danger'); context.setChunkResultStatus('error'); }
        throw new Error(msg);
      }
      key = args[0].trim();
      version = args[1];
      await firstValueFrom(this.#http.delete(this.buildUrl(key), {
        params: new HttpParams().set('version', version.toString()),
      }));
      return true;
    } catch (error: any) {
      const msg = formatStateError('deleteState', error, { key, version });
      if (context) { context.addMessage(msg, 'danger'); context.setChunkResultStatus('error'); }
      throw new Error(msg);
    }
  }
}

/**
 * Format state-service errors so the user gets an actionable message instead
 * of Angular's default "Http failure response for <url>: 409 OK" string.
 *
 * Mirrors enoch/root/src/services/state.service.ts which already does
 * status-specific formatting for 409 conflicts.
 *
 * SCRUM-494: surfaces "Version conflict ... use getState() to fetch the
 * current version first" so QA / authors immediately understand the OCC
 * contract instead of decoding Angular's misleading "409 OK" wording.
 */
function formatStateError(
  op: 'setState' | 'deleteState',
  error: any,
  ctx: { key?: string; version?: number },
): string {
  const status: number | undefined = error?.status;
  const keyLabel = ctx.key ? `"${ctx.key}"` : '<unknown key>';
  const serverBody = extractServerErrorText(error);

  if (status === 409) {
    const expected = typeof ctx.version === 'number' ? ` (expected version ${ctx.version})` : '';
    const hint = ctx.version === 0
      ? 'A state entry with that key already exists. Call getState() first, then setState(key, value, currentVersion).'
      : 'The entry was modified by another writer since you last read it. Re-read with getState() and retry.';
    const bodySuffix = serverBody ? ` Server: ${serverBody}.` : '';
    return `Version conflict in ${op} for ${keyLabel}${expected}.${bodySuffix} ${hint}`;
  }

  if (status === 404 && op === 'deleteState') {
    return `State key ${keyLabel} not found (deleteState).`;
  }

  const baseMessage = (error && (error.message || String(error))) || 'unknown error';
  const serverSuffix = serverBody && !baseMessage.includes(serverBody) ? ` Server: ${serverBody}.` : '';
  return `Error in ${op}: ${baseMessage}.${serverSuffix}`;
}

function extractServerErrorText(error: any): string | null {
  if (!error || typeof error !== 'object') return null;
  const body = (error as { error?: unknown }).error;
  if (!body) return null;
  if (typeof body === 'string') return body.trim() || null;
  if (typeof body === 'object') {
    const obj = body as Record<string, unknown>;
    const candidate =
      (typeof obj['error'] === 'string' && obj['error']) ||
      (typeof obj['message'] === 'string' && obj['message']) ||
      (typeof obj['detail'] === 'string' && obj['detail']);
    if (candidate) return candidate;
    try {
      return JSON.stringify(obj);
    } catch {
      return null;
    }
  }
  return null;
}

